import { observer } from "mobx-react-lite";
import { useState } from "react";

import { describeModel } from "../../describeModel.ts";
import { findProvider, LLM_PROVIDERS } from "../../llmProviders.ts";
import type { Settings } from "../../stores/ConnectionStore.ts";
import { useStores } from "../../stores/RootStore.ts";
import { Input, Label, Muted } from "./primitives.tsx";

export type LlmSettings = Pick<Settings, "llmUrl" | "llmKey" | "llmModel">;

type Props = {
    value: LlmSettings;
    onChange(value: LlmSettings): void;
    /** Whether the form can only be sent with an LLM filled in. */
    required?: boolean;
};

/** The choice in the model list that lets you type a name instead. */
const TYPE_A_NAME = "type a name";

/**
 * Picks the LLM: a provider, its API key and a model. The API address is only asked for with "Other".
 * Models come from the list the main controller sends. You can type a name instead, for models that aren't on it.
 */
export const LlmFields = observer(function LlmFields({ value, onChange, required }: Props) {
    const { connection } = useStores();
    // Which provider is picked follows from the address, except right after picking "Other", when there's none yet.
    const [picked, setPicked] = useState(value.llmUrl ? findProvider(value.llmUrl).id : "");
    // Whether "type a name" was picked from the list. A name that isn't on the list also means typing.
    const [typing, setTyping] = useState(false);
    const provider = LLM_PROVIDERS.find((p) => p.id === picked);
    const models = connection.models[picked] ?? [];
    const listed = models.some((model) => model.id === value.llmModel);
    const choice = typing || (value.llmModel && !listed) ? TYPE_A_NAME : value.llmModel;

    function pick(id: string) {
        const next = LLM_PROVIDERS.find((p) => p.id === id)!;

        setPicked(id);
        setTyping(false);
        // Keys and model names differ per provider. Keeping the old key would also send it to the new provider.
        onChange({ llmUrl: next.url, llmKey: "", llmModel: "" });
    }

    function choose(id: string) {
        setTyping(id === TYPE_A_NAME);
        onChange({ ...value, llmModel: id === TYPE_A_NAME ? "" : id });
    }

    const field = (name: keyof LlmSettings) => ({
        value: value[name],
        onChange: (event: { target: { value: string } }) => onChange({ ...value, [name]: event.target.value }),
    });

    return (
        <>
            <Label>
                Provider
                <Input as="select" value={picked} onChange={(event) => pick(event.target.value)} required={required}>
                    <option value="" disabled>
                        Pick a provider
                    </option>
                    {LLM_PROVIDERS.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                        </option>
                    ))}
                </Input>
            </Label>
            {picked === "other" && (
                <>
                    <Label>
                        API address
                        <Input {...field("llmUrl")} type="url" placeholder="http://my-server:11434/v1" required={required} />
                    </Label>
                    <Muted>Any API that works like OpenAI's chat API. Workers must be able to reach this address.</Muted>
                </>
            )}
            {provider && (
                <>
                    <Label>
                        API key
                        <Input {...field("llmKey")} type="password" placeholder={picked === "other" ? "If your API needs one" : ""} />
                    </Label>
                    {models.length > 0 && (
                        <Label>
                            Model
                            <Input as="select" value={choice} onChange={(event) => choose(event.target.value)} required={required}>
                                <option value="" disabled>
                                    Pick a model
                                </option>
                                {models.map((model) => (
                                    <option key={model.id} value={model.id}>
                                        {describeModel(model)}
                                    </option>
                                ))}
                                <option value={TYPE_A_NAME}>Another model: type its name</option>
                            </Input>
                        </Label>
                    )}
                    {(models.length === 0 || choice === TYPE_A_NAME) && (
                        <Label>
                            {models.length === 0 ? "Model" : "Model name"}
                            <Input {...field("llmModel")} placeholder={`For example ${provider.exampleModel}`} required={required} />
                        </Label>
                    )}
                </>
            )}
        </>
    );
});
